/**
 * 💰 AIRPORT PRICING MANAGEMENT PAGE
 * Admin interface for managing airport transfer pricing for all vehicles
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Grid,
  InputAdornment,
  Pagination,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Flight,
  DirectionsCar,
  FilterList,
  Search,
  AttachMoney,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { airportPricingService, VehicleWithPricing } from '@/services/airportPricingService';
import { airportService, Airport } from '@/services/airportService';
import { airportZoneService, AirportZone } from '@/services/airportZoneService';
import SetAirportPricingDialog from '@/components/pricing/SetAirportPricingDialog';
import ManageVehiclePricingDialog from '@/components/pricing/ManageVehiclePricingDialog';
import CreateVehicleBookingDialog from '@/components/bookings/CreateVehicleBookingDialog';

export const AirportPricingManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState<VehicleWithPricing[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [zones, setZones] = useState<AirportZone[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAirport, setSelectedAirport] = useState<string>('');
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [pricingFilter, setPricingFilter] = useState<'all' | 'with_pricing' | 'without_pricing'>('all');

  // Dialogs
  const [setPricingDialog, setSetPricingDialog] = useState<{ open: boolean; vehicle: VehicleWithPricing | null }>({
    open: false,
    vehicle: null
  });
  const [managePricingDialog, setManagePricingDialog] = useState<{ open: boolean; vehicle: VehicleWithPricing | null }>({
    open: false,
    vehicle: null
  });
  const [createBookingOpen, setCreateBookingOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (selectedAirport) {
      loadZonesForAirport(selectedAirport);
    } else {
      setZones([]);
      setSelectedZone('');
    }
  }, [selectedAirport]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vehiclesResponse, airportsResponse] = await Promise.all([
        airportPricingService.getVehiclesWithPricing({ page, limit: rowsPerPage }),
        airportService.getAirports()
      ]);

      if (vehiclesResponse.success && vehiclesResponse.vehicles) {
        setVehicles(vehiclesResponse.vehicles);

        // Update pagination info
        if (vehiclesResponse.pagination) {
          setTotalVehicles(vehiclesResponse.pagination.total);
          setTotalPages(vehiclesResponse.pagination.totalPages);
        }
      } else {
        toast.error(vehiclesResponse.error || 'Failed to load vehicles');
      }

      if (airportsResponse.success && airportsResponse.airports) {
        setAirports(airportsResponse.airports);
      } else {
        toast.error(airportsResponse.error || 'Failed to load airports');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadZonesForAirport = async (airportId: string) => {
    try {
      const response = await airportZoneService.getZones(airportId);
      if (response.success && response.zones) {
        setZones(response.zones);
      }
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  };

  const handleSetPricing = (vehicle: VehicleWithPricing) => {
    setSetPricingDialog({ open: true, vehicle });
  };

  const handleManagePricing = (vehicle: VehicleWithPricing) => {
    setManagePricingDialog({ open: true, vehicle });
  };

  const handlePricingSet = () => {
    setSetPricingDialog({ open: false, vehicle: null });
    loadData(); // Reload to show updated counts
  };

  const handlePricingUpdated = () => {
    setManagePricingDialog({ open: false, vehicle: null });
    loadData(); // Reload to show updated data
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter(vehicle => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        vehicle.plateNumber.toLowerCase().includes(query) ||
        vehicle.vehicleMake.toLowerCase().includes(query) ||
        vehicle.vehicleModel.toLowerCase().includes(query) ||
        vehicle.hostName?.toLowerCase().includes(query);

      if (!matchesSearch) return false;
    }

    // Pricing filter
    if (pricingFilter === 'with_pricing' && !vehicle.hasAirportPricing) return false;
    if (pricingFilter === 'without_pricing' && vehicle.hasAirportPricing) return false;

    // Airport/zone filter
    if (selectedAirport || selectedZone) {
      if (!vehicle.pricings || vehicle.pricings.length === 0) return false;

      const matchesAirport = !selectedAirport || vehicle.pricings.some(p => p.airportId === selectedAirport);
      const matchesZone = !selectedZone || vehicle.pricings.some(p => p.zoneId === selectedZone);

      return matchesAirport && matchesZone;
    }

    return true;
  });

  const formatCurrency = (amountInKobo: number) => {
    return `₦${(amountInKobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney /> Airport Transfer Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage airport transfer pricing for all vehicles across different zones
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateBookingOpen(true)}
        >
          Create Airport Transfer Booking
        </Button>
      </Box>

      {/* Stats Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Vehicles
              </Typography>
              <Typography variant="h4">
                {vehicles.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                With Airport Pricing
              </Typography>
              <Typography variant="h4" color="success.main">
                {vehicles.filter(v => v.hasAirportPricing).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Without Pricing
              </Typography>
              <Typography variant="h4" color="warning.main">
                {vehicles.filter(v => !v.hasAirportPricing).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Pricing Records
              </Typography>
              <Typography variant="h4">
                {vehicles.reduce((sum, v) => sum + v.airportPricingCount, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search vehicles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Pricing Status</InputLabel>
                <Select
                  value={pricingFilter}
                  label="Pricing Status"
                  onChange={(e) => setPricingFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All Vehicles</MenuItem>
                  <MenuItem value="with_pricing">With Pricing</MenuItem>
                  <MenuItem value="without_pricing">Without Pricing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Airport</InputLabel>
                <Select
                  value={selectedAirport}
                  label="Filter by Airport"
                  onChange={(e) => setSelectedAirport(e.target.value)}
                >
                  <MenuItem value="">All Airports</MenuItem>
                  {airports.map((airport) => (
                    <MenuItem key={airport.id} value={airport.id}>
                      {airport.airportCode} - {airport.airportName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedAirport}>
                <InputLabel>Filter by Zone</InputLabel>
                <Select
                  value={selectedZone}
                  label="Filter by Zone"
                  onChange={(e) => setSelectedZone(e.target.value)}
                >
                  <MenuItem value="">All Zones</MenuItem>
                  {zones.map((zone) => (
                    <MenuItem key={zone.id} value={zone.id}>
                      {zone.zoneName} ({zone.distanceRange.display})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedAirport('');
                  setSelectedZone('');
                  setPricingFilter('all');
                }}
                sx={{ height: '56px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Vehicles Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredVehicles.length === 0 ? (
            <Alert severity="info">
              No vehicles found matching the selected filters.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Plate Number</TableCell>
                    <TableCell>Host</TableCell>
                    <TableCell align="center">Pricing Status</TableCell>
                    <TableCell align="center">Zones Configured</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVehicles.map((vehicle) => (
                    <TableRow key={vehicle.vehicleId} hover>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2" fontWeight={500}>
                            {vehicle.vehicleMake} {vehicle.vehicleModel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vehicle.vehicleYear} • {vehicle.vehicleType}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {vehicle.plateNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {vehicle.hostName || 'Unknown Host'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {vehicle.hasAirportPricing ? (
                          <Chip
                            icon={<CheckCircle />}
                            label="Configured"
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Chip
                            icon={<Cancel />}
                            label="Not Set"
                            size="small"
                            color="warning"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={vehicle.airportPricingCount || 0}
                          size="small"
                          color={vehicle.airportPricingCount > 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {vehicle.hasAirportPricing ? (
                            <Tooltip title="Manage Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleManagePricing(vehicle)}
                                color="primary"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Set Pricing">
                              <IconButton
                                size="small"
                                onClick={() => handleSetPricing(vehicle)}
                                color="success"
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {!loading && filteredVehicles.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, totalVehicles)} of {totalVehicles} vehicles
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(1); // Reset to first page when changing page size
                    }}
                  >
                    <MenuItem value={25}>25 per page</MenuItem>
                    <MenuItem value={50}>50 per page</MenuItem>
                    <MenuItem value={100}>100 per page</MenuItem>
                  </Select>
                </FormControl>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Set Pricing Dialog */}
      {setPricingDialog.vehicle && (
        <SetAirportPricingDialog
          open={setPricingDialog.open}
          vehicle={setPricingDialog.vehicle}
          airports={airports}
          onClose={() => setSetPricingDialog({ open: false, vehicle: null })}
          onSuccess={handlePricingSet}
        />
      )}

      {/* Manage Pricing Dialog */}
      {managePricingDialog.vehicle && (
        <ManageVehiclePricingDialog
          open={managePricingDialog.open}
          vehicle={managePricingDialog.vehicle}
          onClose={() => setManagePricingDialog({ open: false, vehicle: null })}
          onSuccess={handlePricingUpdated}
        />
      )}

      {/* Create Airport Transfer Booking Dialog */}
      <CreateVehicleBookingDialog
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        onSuccess={() => {
          setCreateBookingOpen(false);
          loadData(); // Refresh data after successful booking
        }}
        initialType="airport-transfer"
      />
    </Box>
  );
};

export default AirportPricingManagementPage;
